import { useEffect, useState } from "react";
import styled from "styled-components";
import type { CommonGroupItem, CommonGroupUpsertPayload } from "src/types/common/commonCodeTypes";
import { useCommonCodeStore } from "src/store/commonCode/commonCode.store";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  item?: CommonGroupItem | null;
  onClose: () => void;
};

const emptyPayload: CommonGroupUpsertPayload = {
  grpCode: "",
  grpName: "",
  description: "",
  codeOrder: 0,
  useYn: true,
};

export default function CommonCodeGroupUpsertModal({ open, mode, item, onClose }: Props) {
  const createGroup = useCommonCodeStore((s) => s.createGroup);
  const updateGroup = useCommonCodeStore((s) => s.updateGroup);
  const loading = useCommonCodeStore((s) => s.loading);

  const [payload, setPayload] = useState<CommonGroupUpsertPayload>(emptyPayload);

  useEffect(() => {
    if (!open) return;

    if (mode === "edit" && item) {
      setPayload({
        grpCode: item.grpCode,
        grpName: item.grpName,
        description: item.description || "",
        codeOrder: item.codeOrder,
        useYn: item.useYn ?? true,
      });
      return;
    }

    if (mode === "create") {
      setPayload(emptyPayload);
    }
  }, [open, mode, item]);

  if (!open) return null;

  const onChange = <K extends keyof CommonGroupUpsertPayload>(
    key: K,
    value: CommonGroupUpsertPayload[K]
  ) => {
    setPayload((p) => ({ ...p, [key]: value }));
  };

  const validate = () => {
    if (!payload.grpCode.trim()) return "그룹 코드를 입력해주세요.";
    if (!payload.grpName.trim()) return "그룹명을 입력해주세요.";
    if (payload.codeOrder < 0) return "정렬순은 0 이상이어야 합니다.";
    return null;
  };

  const onSubmit = async () => {
    const err = validate();
    if (err) {
      alert(err);
      return;
    }

    if (mode === "create") {
      const ok = await createGroup(payload);
      if (ok) onClose();
      return;
    }

    if (mode === "edit") {
      if (!item) return;
      const ok = await updateGroup(item.grpCode, payload);
      if (ok) onClose();
    }
  };

  return (
    <Dim onMouseDown={onClose}>
      <Modal onMouseDown={(e) => e.stopPropagation()}>
        <Top>
          <Title>{mode === "create" ? "공통코드 그룹 생성" : "공통코드 그룹 수정"}</Title>
          <XBtn type="button" onClick={onClose}>
            ✕
          </XBtn>
        </Top>

        <Body>
          <Grid>
            <Field>
              <Label>그룹 코드</Label>
              <Input
                value={payload.grpCode}
                disabled={mode === "edit"}
                onChange={(e) => onChange("grpCode", e.target.value)}
                placeholder="예: TEST"
              />
              {mode === "edit" && <Help>그룹 코드는 수정할 수 없습니다.</Help>}
            </Field>

            <Field>
              <Label>그룹명</Label>
              <Input
                value={payload.grpName}
                onChange={(e) => onChange("grpName", e.target.value)}
                placeholder="예: 테스트 그룹"
              />
            </Field>

            <Field>
              <Label>정렬순</Label>
              <Input
                type="number"
                value={payload.codeOrder}
                onChange={(e) => onChange("codeOrder", Number(e.target.value))}
              />
            </Field>

            <FieldRow>
              <Check>
                <input
                  type="checkbox"
                  checked={payload.useYn}
                  onChange={(e) => onChange("useYn", e.target.checked)}
                />
                <span>사용</span>
              </Check>
            </FieldRow>

            <FieldFull>
              <Label>설명</Label>
              <Textarea
                value={payload.description}
                onChange={(e) => onChange("description", e.target.value)}
                rows={3}
              />
            </FieldFull>
          </Grid>
        </Body>

        <Bottom>
          <GhostBtn type="button" onClick={onClose} disabled={loading}>
            취소
          </GhostBtn>
          <PrimaryBtn type="button" onClick={onSubmit} disabled={loading}>
            {loading ? "처리중..." : mode === "create" ? "생성" : "수정"}
          </PrimaryBtn>
        </Bottom>
      </Modal>
    </Dim>
  );
}

/* ================= styles ================= */

const Dim = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const Modal = styled.div`
  width: 520px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
`;

const Top = styled.div`
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font-size: 16px;
  font-weight: 900;
`;

const XBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid #eee;
  background: #fff;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 16px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldRow = styled.div`
  grid-column: 1 / -1;
  display: flex;
  gap: 16px;
  align-items: center;
`;

const FieldFull = styled.div`
  grid-column: 1 / -1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.div`
  font-size: 12px;
  color: #666;
`;

const Help = styled.div`
  font-size: 12px;
  color: #999;
`;

const Input = styled.input`
  height: 38px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
`;

const Textarea = styled.textarea`
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 10px;
  resize: vertical;
`;

const Check = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`;

const Bottom = styled.div`
  padding: 14px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const Btn = styled.button`
  height: 38px;
  padding: 0 14px;
  border-radius: 10px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: #fff;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const GhostBtn = styled(Btn)``;

const PrimaryBtn = styled(Btn)`
  background: #111;
  color: #fff;
  border: 0;
`;
